import { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, ChevronUp, Plus, Search } from 'lucide-react'
import { useProductStore } from '../store/productStore'
import { useAdminFilters } from '../hooks/useAdminFilters'
import AdminFiltersBar from '../features/backoffice/AdminFiltersBar'
import AdminVirtualList, { type VirtualListHandle } from '../features/backoffice/AdminVirtualList'
import AdminProductRow from '../features/backoffice/AdminProductRow'
import AdminSkeleton from '../layout/skeletons/AdminSkeleton'
import CategoriesManager from '../features/backoffice/CategoriesManager'
import { motion } from 'framer-motion'
import { useMediaQuery } from '../hooks/useMediaQuery'

type Tab = 'products' | 'categories'

export default function AdminPage() {
  const { isLoading } = useProductStore()
  const navigate = useNavigate()
  const filters = useAdminFilters()
  const [activeTab, setActiveTab] = useState<Tab>('products')

  const adminListRef = useRef<VirtualListHandle>(null)
  const [virtualScrolled, setVirtualScrolled] = useState(false)

  const isMobile  = useMediaQuery('(max-width: 639px)')
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const isLandscape = !isMobile && !isDesktop;

  const FIT_MEDIA_VIRTUAL_THRESHOLD = isMobile || isLandscape ? 3 : 5;
  const useVirtual = filters.filtered.length >= FIT_MEDIA_VIRTUAL_THRESHOLD
  
  // useEffect(() => {
  //   localStorage.setItem('imarket_banner_dismissed', 'true')
  // }, [])

  return (
    <div dir="rtl" className="min-h-screen bg-cream">
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 70% 50% at 95% 5%, rgba(82,183,136,0.10) 0%, transparent 60%),
            radial-gradient(ellipse 50% 40% at 5% 90%, rgba(244,132,95,0.08) 0%, transparent 55%)
          `
        }}
      />

      <main className="relative z-10 max-w-[1200px] mx-auto pt-10 pb-2">

        {/* Top bar */}
        <div className="flex items-center justify-between gap-2 mb-6 px-4">
          <div>
            <div className="flex items-center gap-2 lg:gap-3.5">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-4 py-2 rounded-[10px] text-sm font-medium text-text-secondary bg-white border-none cursor-pointer shadow-[0_2px_8px_rgba(44,24,16,0.07)] hover:shadow-[0_4px_16px_rgba(44,24,16,0.12)] transition-shadow"
            >
              <ArrowRight className="h-4 w-4" />
              קטלוג
            </button>
            
            <button
              onClick={() => navigate('/admin/add')}
              className="flex items-center gap-2 px-5 py-2 rounded-[10px] text-sm font-semibold text-[#2D6A4F] border border-[#2D6A4F] cursor-pointer hover:shadow-[0_4px_12px_rgba(45,106,79,0.28)] transition-[box-shadow] duration-200"
            >
              <span className="hidden lg:block">הוסף מוצר</span>
              <Plus className="h-4 w-4" />
            </button>

            </div>
          </div>
          <div className="flex items-center gap-2 lg:gap-3.5">
            <button
              onClick={() => setActiveTab('products')}
              className={`px-5 py-2 rounded-[10px] text-sm font-medium transition-colors cursor-pointer border-none ${
                activeTab === 'products'
                  ? 'bg-green-primary text-white shadow-[0_2px_8px_rgba(45,106,79,0.25)]'
                  : 'bg-white text-text-secondary hover:bg-cream'
              }`}
            >
              מוצרים
            </button>
            <button
              onClick={() => setActiveTab('categories')}
              className={`px-5 py-2 rounded-[10px] text-sm font-medium transition-colors cursor-pointer border-none ${
                activeTab === 'categories'
                  ? 'bg-green-primary text-white shadow-[0_2px_8px_rgba(45,106,79,0.25)]'
                  : 'bg-white text-text-secondary hover:bg-cream'
              }`}
            >
              קטגוריות
            </button>
          </div>
        </div>

        {/* Products Tab */}
        {activeTab === 'products' && (
          <>
            <AdminFiltersBar
              search={filters.search}
              setSearch={filters.setSearch}
              statusFilter={filters.statusFilter}
              setStatusFilter={filters.setStatusFilter}
              sortField={filters.sortField}
              toggleSort={filters.toggleSort}
              sortIcon={filters.sortIcon}
              hasActiveFilters={filters.hasActiveFilters}
              clearFilters={filters.clearFilters}
              totalCount={filters.totalCount}
              filteredCount={filters.filtered.length}
            />
            

            {isLoading && <AdminSkeleton />}

            {!isLoading && filters.filtered.length === 0 && (
              <div className="text-center py-24 text-text-muted">
                <div className="w-full flex items-center justify-center mb-8" aria-hidden="true">
                  <Search className="h-8 w-8" />
                </div>
                <p className="text-base">לא נמצאו מוצרים</p>
              </div>
            )}

            {!isLoading && filters.filtered.length > 0 && useVirtual && (
              <AdminVirtualList
                ref={adminListRef}
                items={filters.filtered}
                onEdit={id => navigate(`/admin/edit/${id}`)}
                onScrollChange={setVirtualScrolled}
              />
            )}

            {!isLoading && filters.filtered.length > 0 && !useVirtual && (
              <div className="flex flex-col gap-4 px-4 my-2 lg:my-2 py-2 lg:py-1">
                {filters.filtered.map(product => (
                  <AdminProductRow
                    key={product.id}
                    product={product}
                    onEdit={() => navigate(`/admin/edit/${product.id}`)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="px-6 lg:px-4">
            <h2 className="font-serif text-[28px] lg:text-[32px] font-bold text-text-primary tracking-tight leading-none mb-6">
            {/* <h2 className="font-serif text-[22px] font-bold text-text-primary mb-6"> */}
              ניהול קטגוריות
            </h2>
            <CategoriesManager />
          </div>
        )}
      </main>
 

      {useVirtual && virtualScrolled && activeTab === 'products' && (
        <motion.button
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          onClick={() => adminListRef.current?.scrollToTop()}
          className="fixed bottom-6 left-6 z-50 w-10 h-10 rounded-full bg-green-primary text-white border-none cursor-pointer shadow-[0_4px_14px_rgba(45,106,79,0.35)] flex items-center justify-center hover:brightness-110 transition-[filter] duration-500"
          aria-label="חזרה למעלה"
        >
          <ChevronUp className="h-5 w-5" />
        </motion.button>
      )}

    </div>
  )
}
